import { MIME_TYPES } from '@mantine/dropzone'
import type { BaseReactProps, ReactNodeDef } from '@shared/node-v1.0.0'
import { getPortDef, sizes } from '@shared/port-v1.0.0'
import { lazy } from 'react'

export type Props = BaseReactProps & {
	fileTypes: 'image' | 'pdf' | 'excel' | 'word' | 'presentation' | 'custom' | 'any'
	customFileTypes?: string[]
	maxFileSize?: number
	multiple: boolean
}

export default {
	hashTag: '#pre-release',
	module: { dynamic: lazy(() => import('../component')) },
	inNode: {
		inputs: [
			getPortDef({
				name: 'fileTypes',
				displayName: 'File types',
				group: 'Custom',
				customGroup: 'File types',
				type: [
					{ label: 'Image', value: 'image' },
					{ label: 'PDF', value: 'pdf' },
					{ label: 'Excel', value: 'excel' },
					{ label: 'Word', value: 'word' },
					{ label: 'Presentation', value: 'presentation' },
					{ label: 'Custom', value: 'custom' },
					{ label: 'Any', value: 'any' },
				],
				default: 'image',
			}),
			getPortDef({
				name: 'customFileTypes',
				displayName: 'Custom file types',
				group: 'Custom',
				customGroup: 'Custom file types',
				type: 'proplist',
				dependsOn: (p: Props) => p.fileTypes === 'custom',
				validate: (p: Props) => {
					if (p.fileTypes !== 'custom') return true
					if (!p.customFileTypes?.length) return 'Custom file types are required'

					const invalidTypes: string[] = []
					for (const type of p.customFileTypes || []) {
						if (!Object.keys(MIME_TYPES).includes(type as keyof typeof MIME_TYPES)) {
							invalidTypes.push(type)
						}
					}

					if (invalidTypes.length) return `This types not supported: ${invalidTypes.join(', ')}`
					return true
				},
			}),
			getPortDef({ name: 'maxFileSize', displayName: 'Max file szie (MB)', group: 'Params', type: 'number', default: 5 }),
			getPortDef({ name: 'multiple', displayName: 'Multiple', group: 'Params', type: 'boolean', default: true }),
			getPortDef({
				name: 'maxFiles',
				displayName: 'Max files',
				group: 'Params',
				type: 'number',
				default: 5,
				dependsOn: (p: Props) => p.multiple,
			}),
			getPortDef({ name: 'w', displayName: 'Width', group: 'Dimensions', type: 'string' }),
			getPortDef({ name: 'radius', displayName: 'Radius', group: 'Styles', type: sizes, default: 'md' }),
			getPortDef({ name: 'acceptColor', displayName: 'Accept color', group: 'Styles', type: 'string', default: 'blue' }),
			getPortDef({ name: 'rejectColor', displayName: 'Reject color', group: 'Styles', type: 'string', default: 'red' }),
			getPortDef({ name: 'loading', displayName: 'Loading', group: 'States', type: 'boolean', default: false }),
			getPortDef({ name: 'disabled', displayName: 'Disabled', group: 'States', type: 'boolean', default: false }),
			getPortDef({ name: 'reset', displayName: 'Reset', group: 'Signals', type: 'signal' }),
		],
		outputs: [
			getPortDef({ name: 'accepted', displayName: 'Accepted', group: 'Signals', type: 'signal' }),
			getPortDef({ name: 'acceptedFiles', displayName: 'Accepted files', group: 'Data', type: 'array' }),
			getPortDef({ name: 'rejected', displayName: 'Rejected', group: 'Signals', type: 'signal' }),
			getPortDef({ name: 'rejectedFiles', displayName: 'Rejected files', group: 'Data', type: 'array' }),
			getPortDef({ name: 'reseted', displayName: 'Reseted', group: 'Signals', type: 'signal' }),
		],
	},
} satisfies ReactNodeDef
